import { clsx } from "clsx";

import type { EventFragment } from "~/__generated__/graphql/graphql";
import { centsToMoney } from "~/utils/money";
import { useRootLocale } from "~/root";
import {
  getSelectedTicketVariation,
  getTicketVariationAvailabilityLabel,
  getVisibleTicketVariations,
} from "./ticketVariations";

type Props = {
  plan: EventFragment;
  enabled: boolean;
  selectedVariationID: string | null;
  onSelect: (variationID: string) => void;
};

export function TicketVariationSelector({
  plan,
  enabled,
  selectedVariationID,
  onSelect,
}: Props) {
  const locale = useRootLocale();

  if (!enabled) return null;

  const variations = getVisibleTicketVariations(plan);
  const selected = getSelectedTicketVariation(plan, selectedVariationID);

  return (
    <div
      className="flex flex-col gap-2"
      role="radiogroup"
      aria-label="Ticket options"
    >
      {variations.map((variation) => {
        const isSelected = variation.id === selected?.id;
        const availability = getTicketVariationAvailabilityLabel(variation);

        return (
          <button
            key={variation.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={!variation.available}
            onClick={() => onSelect(variation.id)}
            className={clsx(
              "flex w-full items-center gap-3 rounded-[9px] border px-3 py-2.5 text-left transition-colors",
              isSelected
                ? "border-butter-green bg-butter-green text-butter-cream-light"
                : "border-butter-cream bg-white/70 text-butter-green hover:bg-white",
              variation.available
                ? "cursor-pointer"
                : "cursor-not-allowed opacity-50",
            )}
          >
            <span
              aria-hidden
              className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-butter-cream text-xl"
            >
              {variation.emoji}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-sans text-base font-bold wrap-break-word">
                {variation.name}
              </span>
              {variation.description && (
                <span className="mt-0.5 block font-sans text-sm leading-snug">
                  {variation.description}
                </span>
              )}
              {availability && (
                <span
                  className={clsx(
                    "mt-1 block font-sans text-sm font-normal",
                    isSelected ? "text-butter-cream-light" : "text-butter-pink",
                  )}
                >
                  {availability}
                </span>
              )}
            </span>
            <span className="shrink-0 font-sans text-base font-bold">
              {centsToMoney(variation.totalCostCents, "$", locale)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
